import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import mountainBack from "../../assets/images/hero-image-10.jpg";
import mountainFront from "../../assets/images/hero-image-12.jpg";
import TypingText from "../utils/TypingText";
import Counter from "../utils/Counter";

export default function MountainExperience() {
  const parallaxRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start start", "end start"],
  });

  const maskY = useTransform(scrollYProgress, [0, 0.5], [0, -200]);
  const maskScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.3]);
  const maskOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const frontY = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const backY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      ref={parallaxRef}
      className="relative w-full h-[120dvh] md:h-[170vh] overflow-hidden bg-white"
    >
      {/* Background */}
      <motion.img
        src={mountainBack}
        alt="Distant Mountains"
        style={{ y: backY }}
        className="absolute bottom-0 w-full h-full object-cover z-10"
      />

      {/* Foreground */}
      <motion.img
        src={mountainFront}
        alt="Foreground Mountains"
        style={{ y: maskY, scale: maskScale, opacity: maskOpacity }}
        className="absolute bottom-0 w-full h-full object-cover z-20 pointer-events-none"
      />

      {/* Center Heading */}
    <motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 1.2, ease: "easeOut" }}
  style={{ opacity: maskOpacity }}
  className="
    section-info-heading absolute
    left-0 w-full
    text-center sm:text-right
    xl:left-auto xl:right-10 xl:w-auto
    top-[14%] sm:top-[16%] md:top-[12%]
    z-30 px-6
  "
>
  <a
    href="./"
    className="inline-block border-t mr-[clamp(0.5rem,3vw,4rem)] border-gray-300/50 overflow-hidden"
  >
    <motion.div
      initial={{ y: '100%', opacity: 0 }}
      animate={{ y: '0%', opacity: 1 }}
      transition={{ duration: 1.5, ease: 'easeOut' }}
    >
      <h2 className="font-bold text-gray-200 drop-shadow-md text-[clamp(2rem,4vw,5rem)]">
        INTO UNTAMED
      </h2>
    </motion.div>

    <motion.div
      initial={{ y: '100%', opacity: 0 }}
      animate={{ y: '0%', opacity: 1 }}
      transition={{ duration: 1.5, ease: 'easeOut' }}
    >
      <h2 className="font-bold text-gray-200 drop-shadow-md text-[clamp(2rem,4vw,5rem)]">
        HORIZION
      </h2>
    </motion.div>
  </a>

  <p className="mt-3 text-gray-300 mr-[clamp(0.5rem,3vw,4rem)] opacity-90 text-[clamp(1rem,1.6vw,3.5rem)]">
    <TypingText text="Watch the layers of nature reveal themselves across 14 majestic peaks" />
  </p>

  <div
    className="
      mt-6 flex flex-wrap
      justify-center sm:justify-end 
      items-end gap-2 md:space-x-4
      leading-[0.9] tracking-tight
      text-[clamp(1.8rem,3.2vw,5rem)]
      mr-[clamp(0.5rem,3vw,4rem)]
    "
  >
    <Counter
      from={0}
      to={14}
      duration={2.5}
      suffix=" Peaks"
      color="white"
      className="drop-shadow-lg"
    />

    <span className="text-white drop-shadow-lg opacity-80">
      Conquer the
    </span>

    <Counter
      from={0}
      to={8000}
      duration={2.5}
      suffix=" ers"
      color="white"
      className="drop-shadow-lg"
    />
  </div>
    </motion.div>


      {/* Right Panel */}
      <div className="absolute inset-0 z-[25]  flex flex-col items-end justify-end p-[clamp(1rem,4vw,3rem)] pointer-events-none">
        <h3
          className="
            font-bold text-gray-300 leading-none select-none mb-4 text-right
            mr-[clamp(0.5rem,3vw,4rem)]
            min-[1700px]:text-[25rem]
            min-[1300px]:max-[1699px]:text-[clamp(150px,10vw,285px)]
            max-[1299px]:text-[clamp(80px,8vw,200px)]
            max-[768px]:text-[clamp(52px,13vw,120px)]

          "
        >
          ADVENTURE
        </h3>

        <div className="max-w-md pointer-events-auto text-right mb-[clamp(1rem,4vw,6rem)] mr-[clamp(0.5rem,3vw,4rem)]">
          <p className="uppercase tracking-widest font-bold text-gray-500 mb-1 text-[clamp(0.75rem,1vw,0.9rem)]">
            EXPLORE
          </p>

          <h2 className="text-white font-bold mb-2 drop-shadow-lg text-[clamp(1.8rem,3vw,3rem)]">
            The Great Outdoors
          </h2>

          <p className="text-gray-600 mb-4 drop-shadow-lg text-[clamp(0.95rem,1.4vw,1.1rem)]">
            Join us for thrilling outdoor adventures, scenic escapes, and unforgettable
            moments in nature. From weekend getaways to extended journeys, explore the
            world with BB Adventures.
          </p>

          <a
            href="./contact"
            className="px-6 py-2 rounded-full border border-gray-600/80 text-gray-600/80 font-medium hover:bg-white hover:text-gray-800 transition inline-block text-[clamp(0.85rem,1vw,1rem)]"
          >
            JOIN
          </a>
        </div>
      </div>
    </section>
  );
}
